/**
 * LexiStar report API client (ST-00188 M4-R1 engine, M4-R2 pages; docs/lexistar/contracts/
 * m4-contracts.md §6, §7; runbook docs/lexistar/runbooks/reports.md). Server:
 * server/modules/lexistar/reports, mounted at /api/lexistar, + POST /api/auth/report-session.
 * The one client of the report engine: the report pages, the Reports centre and the schedule
 * editor all call it. Shapes: `types/lexistar-reports` (the client's reading of the bodies;
 * the server declares them in server/types/lexistar-reports.ts).
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`; inside the renderer shell
 * also the report-session bearer, Reports/reportSession.ts). Bodies are read tolerantly at the
 * edge (`pick*`) and normalised, so a page never trips over a missing list.
 *
 *   listDefinitions()                        GET  /reports                 → ReportDefinition[]
 *   getReportDefinition(key)                 GET  /reports/:key            → { definition, schedules_allowed }
 *   getReportData(key, parameters)           GET  /reports/:key/data       → { data, pinned }  (live, not stored)
 *   getRunData(runId)                        GET  /reports/runs/:id/data   → { parameters, data, pinned }
 *   runReport(key, {parameters, formats})    POST /reports/:key:run        → ReportRun         (202, queued)
 *   listRuns(query) / getRun(id)             GET  /reports/runs[/:id]
 *   waitForRun(id, {…})                      polls getRun until done | failed
 *   resolveDownload(id, format)              GET  /reports/runs/:id/download → { url, filename } (signed, short-lived)
 *   downloadHref / startDownload / runPrintUrl
 *   listSchedules / createSchedule / updateSchedule / deleteSchedule / runScheduleNow
 *   listUserOptions / listGroupOptions       recipients' reference lists (GET /users, /recipient-groups)
 *   certificatePdfUrl(parallelRunId)         URL of the M3 certificate PDF (cookie session)
 *   errorText / statusOf / isObj / pickList / pickObject / cleanParams   edge helpers
 *
 * The report pages read KPIs through services/lexistarAnalytics and trip cost through
 * services/lexistarCosts — the same clients the dashboard and trip detail use.
 *
 * PRINT MODE (what lexistar-renderer opens): <APP origin><definition.page>?print=1&run=<run_id>,
 * having ALREADY exchanged its one-time report token for a cookie session (POST
 * /api/auth/report-session), so the page is simply signed in as the report's user. The page
 * drops the app chrome (`readPrintParams(location.search).print`), loads the run's snapshot
 * with `getRunData(runId)` (the same data as the run's XLSX / CSV), and signals
 * `data-report-ready="true"` once everything is drawn, or `data-report-error` (the report
 * pages' ReportFrame does both; `markReportReady` / `markReportError` for any other page). The
 * renderer waits 60–90 s for either and fails the PDF with the message. A print link opened
 * outside the renderer with `?rt=<token>` can call `exchangeReportToken(token)` first; the
 * report pages' own `?report_session=` handling is Reports/reportSession.ts.
 */
import api from './api';
import type {
  DownloadLink,
  ReportDefinition,
  ReportFormat,
  ReportRun,
  ReportSchedule,
  ReportSessionAnswer,
  JsonSchema,
  RunListQuery,
  ScheduleDraft,
} from '../types/lexistar-reports';

const LX = '/lexistar';
const enc = encodeURIComponent;

type Obj = Record<string, unknown>;

// ── Edge helpers ────────────────────────────────────────────────────────────────────

export const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

/** First array under `keys` (or the body itself when it is an array). */
export function pickList<T = Obj>(data: unknown, ...keys: string[]): T[] {
  if (Array.isArray(data)) return data as T[];
  if (!isObj(data)) return [];
  for (const k of [...keys, 'data', 'rows', 'items']) {
    const v = data[k];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

/** First object under `keys`, else the body itself. */
export function pickObject<T = Obj>(data: unknown, ...keys: string[]): T | null {
  if (!isObj(data)) return null;
  for (const k of keys) {
    const v = data[k];
    if (isObj(v)) return v as T;
  }
  return data as T;
}

/**
 * Query-string form of parameters: drops undefined / null / '' so the API never sees
 * `trip_id=`; arrays become comma lists (the server splits `*_ids`, `stage`, … lists) and
 * objects JSON (the server parses JSON-looking values).
 */
export function cleanParams(params: object = {}): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
      return;
    }
    if (isObj(v)) {
      out[k] = JSON.stringify(v);
      return;
    }
    out[k] = v as string | number | boolean;
  });
  return out;
}

const num = (v: unknown): number | null =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null;
const str = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);

/** The API's error text, else `fallback`. */
export function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: Obj }; message?: string } | null;
  const d = e?.response?.data;
  const msg = (d && (str(d.error) || str(d.message))) || null;
  if (msg) return msg;
  if (e?.response?.status === 403) return 'You do not have permission for this.';
  return fallback;
}

export const statusOf = (err: unknown): number | null =>
  (err as { response?: { status?: number } } | null)?.response?.status ?? null;

/** A server path (`/api/...`) as an absolute URL on the API's origin. */
export function absoluteApiUrl(path: string): string {
  const base = String(api.defaults.baseURL || '/api');
  try {
    return new URL(path, new URL(base, window.location.origin)).toString();
  } catch {
    return path;
  }
}

// ── Definitions, provider data ──────────────────────────────────────────────────────

function normaliseDefinition(d: Obj): ReportDefinition {
  const formats = Array.isArray(d.formats) ? (d.formats as ReportFormat[]) : [];
  let parameters: JsonSchema | null = null;
  if (isObj(d.parameters)) parameters = d.parameters as JsonSchema;
  else if (typeof d.parameters === 'string') {
    try {
      parameters = JSON.parse(d.parameters) as JsonSchema;
    } catch {
      parameters = null;
    }
  }
  return {
    key: String(d.key),
    name: str(d.name) || String(d.key),
    description: str(d.description),
    formats,
    parameters,
    permission: str(d.permission),
    page: str(d.page),
    active: d.active !== false,
  };
}

/** The definitions the caller may run. */
export async function listDefinitions(): Promise<ReportDefinition[]> {
  const res = await api.get(`${LX}/reports`);
  return pickList<Obj>(res.data, 'reports', 'definitions')
    .filter((d) => d && d.key)
    .map(normaliseDefinition);
}

/** One definition, and whether the caller may schedule it. */
export async function getReportDefinition(
  key: string
): Promise<{ definition: ReportDefinition; schedules_allowed: boolean }> {
  const res = await api.get(`${LX}/reports/${enc(key)}`);
  const d = pickObject<Obj>(res.data, 'definition');
  if (!d || !d.key) throw new Error('Report definition not found.');
  return {
    definition: normaliseDefinition(d),
    schedules_allowed: isObj(res.data) && res.data.schedules_allowed === true,
  };
}

/** Provider data for a standard report, live (not stored): { data, pinned }. */
export async function getReportData(
  key: string,
  parameters: Obj
): Promise<{ data: unknown; pinned: Obj | null }> {
  const res = await api.get(`${LX}/reports/${enc(key)}/data`, {
    params: cleanParams(parameters),
  });
  const body = res.data as Obj | unknown[];
  if (isObj(body) && 'data' in body) {
    return { data: body.data, pinned: isObj(body.pinned) ? body.pinned : null };
  }
  return { data: body, pinned: null };
}

/**
 * A run's data snapshot — what the renderer prints (M4-R1 opens a report page
 * as `<page>?print=1&run=<run_id>`): { run: {id, report_key, state, parameters, …},
 * data, pinned }. The figures are the ones the run pinned, so the PDF is
 * reproducible (LS-04 §12).
 */
export async function getRunData(
  runId: string
): Promise<{ parameters: Obj; data: unknown; pinned: Obj | null }> {
  const res = await api.get(`${LX}/reports/runs/${runId}/data`);
  const body = isObj(res.data) ? res.data : {};
  const run = isObj(body.run) ? body.run : {};
  return {
    parameters: isObj(run.parameters) ? run.parameters : {},
    data: body.data,
    pinned: isObj(body.pinned) ? body.pinned : null,
  };
}

// ── Runs ────────────────────────────────────────────────────────────────────────────

function normaliseRun(r: Obj): ReportRun {
  return {
    ...(r as unknown as ReportRun),
    id: String(r.id),
    report_key: String(r.report_key ?? r.key ?? ''),
    state: (str(r.state) || 'queued') as ReportRun['state'],
    formats: Array.isArray(r.formats) ? (r.formats as ReportFormat[]) : [],
    artefacts: pickList(r.artefacts) as ReportRun['artefacts'],
    parameters: isObj(r.parameters) ? r.parameters : {},
  };
}

export interface RunReportInput {
  parameters: Obj;
  formats: ReportFormat[];
  /** Global admins only: run it for another organisation. */
  tenant_id?: number;
}

export async function runReport(key: string, { parameters, formats, tenant_id }: RunReportInput): Promise<ReportRun> {
  const res = await api.post(`${LX}/reports/${enc(key)}:run`, {
    parameters,
    formats,
    ...(tenant_id ? { tenant_id } : {}),
  });
  const run = pickObject<Obj>(res.data, 'run', 'report_run');
  if (!run || !run.id) throw new Error('The server did not return a report run.');
  return normaliseRun(run);
}

/** Run history, newest first ({ runs, total, limit, offset }). */
export async function listRuns(
  params: RunListQuery = {}
): Promise<{ runs: ReportRun[]; total: number | null }> {
  const res = await api.get(`${LX}/reports/runs`, { params: cleanParams(params) });
  const runs = pickList<Obj>(res.data, 'runs').map(normaliseRun);
  const body = isObj(res.data) ? res.data : {};
  const pag = isObj(body.pagination) ? body.pagination : null;
  return { runs, total: num(body.total) ?? (pag ? num(pag.total) : null) };
}

export async function getRun(id: string): Promise<ReportRun> {
  const res = await api.get(`${LX}/reports/runs/${id}`);
  const run = pickObject<Obj>(res.data, 'run', 'report_run');
  if (!run || !run.id) throw new Error('Report run not found.');
  return normaliseRun(run);
}

/** The browser-facing download address (the server answers 302 → signed URL, or streams). */
export function downloadHref(runId: string, format: ReportFormat): string {
  const base = String(api.defaults.baseURL || '/api').replace(/\/+$/, '');
  return `${base}${LX}/reports/runs/${runId}/download?format=${encodeURIComponent(format)}`;
}

/**
 * Where to send the browser for an artefact. GET /runs/:id/download answers
 * JSON — { url, filename, expires_at, … } (M4-R1 DownloadLink: a signed,
 * short-lived link to the server's own /file route, so no session is needed to
 * follow it). A server that redirects or streams instead is followed directly.
 */
export async function resolveDownload(
  runId: string,
  format: ReportFormat
): Promise<{ url: string; filename: string | null }> {
  try {
    const res = await api.get(`${LX}/reports/runs/${runId}/download`, {
      params: { format },
      headers: { Accept: 'application/json' },
    });
    const body = res.data as unknown;
    const link = pickObject<Partial<DownloadLink> & Obj>(body, 'link', 'download');
    const url = link && (str(link.url) || str(link.path) || str(link.href));
    if (url) return { url, filename: str(link.filename) };
  } catch {
    /* fall through to the route itself */
  }
  return { url: downloadHref(runId, format), filename: null };
}

/** Send the browser to a download (a new tab is blocked after an await, an anchor is not). */
export function startDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** URL of the server's own HTML print view of a run (the PDF fallback page). */
export const runPrintUrl = (id: string): string =>
  absoluteApiUrl(`/api/lexistar/reports/runs/${enc(id)}/print`);

export const TERMINAL_STATES = ['done', 'failed'];

/** Poll a run until it is done or failed (or the time runs out). */
export async function waitForRun(
  id: string,
  {
    intervalMs = 1500,
    timeoutMs = 120000,
    onUpdate,
    sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
  }: {
    intervalMs?: number;
    timeoutMs?: number;
    onUpdate?: (run: ReportRun) => void;
    sleep?: (ms: number) => Promise<void>;
  } = {}
): Promise<ReportRun> {
  const started = Date.now();
  for (;;) {
    const run = await getRun(id);
    onUpdate?.(run);
    if (TERMINAL_STATES.includes(run.state)) return run;
    if (Date.now() - started > timeoutMs) {
      throw new Error('The report is taking longer than expected — find it under Reports › Runs.');
    }
    await sleep(intervalMs);
  }
}

// ── Schedules ───────────────────────────────────────────────────────────────────────

function normaliseSchedule(s: Obj): ReportSchedule {
  return {
    id: String(s.id),
    tenant_id: num(s.tenant_id) ?? undefined,
    report_key: String(s.report_key ?? ''),
    name: str(s.name) || 'Schedule',
    parameters: isObj(s.parameters) ? s.parameters : {},
    cron: str(s.cron) || '',
    tz: str(s.tz) || 'Africa/Johannesburg',
    formats: Array.isArray(s.formats) ? (s.formats as ReportFormat[]) : [],
    recipients: pickList(s.recipients) as ReportSchedule['recipients'],
    channels: Array.isArray(s.channels) ? (s.channels as ReportSchedule['channels']) : [],
    active: s.active !== false,
    last_run_at: str(s.last_run_at),
    next_run_at: str(s.next_run_at),
    version: num(s.version) ?? undefined,
  };
}

export async function listSchedules(): Promise<ReportSchedule[]> {
  const res = await api.get(`${LX}/reports/schedules`);
  return pickList<Obj>(res.data, 'schedules').map(normaliseSchedule);
}

export async function createSchedule(draft: ScheduleDraft): Promise<ReportSchedule> {
  const res = await api.post(`${LX}/reports/schedules`, draft);
  return normaliseSchedule(pickObject<Obj>(res.data, 'schedule') || {});
}

/** Patch; pass the schedule's `version` to refuse a stale edit (409 code stale_version). */
export async function updateSchedule(
  id: string,
  patch: Partial<ScheduleDraft>
): Promise<ReportSchedule> {
  const res = await api.patch(`${LX}/reports/schedules/${id}`, patch);
  return normaliseSchedule(pickObject<Obj>(res.data, 'schedule') || {});
}

export async function deleteSchedule(id: string): Promise<ReportSchedule> {
  const res = await api.delete(`${LX}/reports/schedules/${enc(id)}`);
  return normaliseSchedule(pickObject<Obj>(res.data, 'schedule') || {});
}

/** POST /reports/schedules/:id:runNow → 202 { runs } (one per recipient scope); the first. */
export async function runScheduleNow(id: string): Promise<ReportRun | null> {
  const res = await api.post(`${LX}/reports/schedules/${id}:runNow`, {});
  const runs = pickList<Obj>(res.data, 'runs');
  const run = runs[0] || pickObject<Obj>(res.data, 'run');
  return run && run.id ? normaliseRun(run) : null;
}

/** Cron presets for a schedule (SAST) with the relative period each one reports on. */
export const CRON_PRESETS: ReadonlyArray<{ label: string; cron: string; period: string }> = [
  { label: 'Daily at 06:00', cron: '0 6 * * *', period: 'yesterday' },
  { label: 'Weekdays at 06:00', cron: '0 6 * * 1-5', period: 'yesterday' },
  { label: 'Weekly, Monday 06:00', cron: '0 6 * * 1', period: 'last_week' },
  { label: 'Monthly, 1st at 06:00', cron: '0 6 1 * *', period: 'last_month' },
  { label: 'Shift end, 18:00', cron: '0 18 * * *', period: 'today' },
];

// ── Recipients' reference lists (users, recipient groups) ───────────────────────────

export interface UserOption {
  id: string;
  label: string;
}

export async function listUserOptions(): Promise<UserOption[]> {
  const res = await api.get('/users', { params: { status: 'active' } });
  return pickList<Obj>(res.data, 'users').map((u) => ({
    id: String(u.user_id ?? u.id),
    label:
      [u.first_name, u.last_name].filter(Boolean).join(' ') ||
      str(u.username) ||
      str(u.email) ||
      `User ${String(u.user_id ?? u.id)}`,
  }));
}

export async function listGroupOptions(): Promise<UserOption[]> {
  const res = await api.get(`${LX}/recipient-groups`);
  return pickList<Obj>(res.data, 'groups').map((g) => ({
    id: String(g.id),
    label: str(g.name) || String(g.id),
  }));
}

// ── Certificate (M3) ────────────────────────────────────────────────────────────────

/** GET URL of the Trip Reconciliation Certificate PDF (cookie session; rendered on first use). */
export const certificatePdfUrl = (parallelRunId: string, { inline = false }: { inline?: boolean } = {}): string =>
  absoluteApiUrl(`/api/lexistar/parallel-runs/${enc(parallelRunId)}/certificate.pdf${inline ? '?download=false' : ''}`);

// ── Print mode (see the header) ─────────────────────────────────────────────────────

export interface PrintParams {
  print: boolean;
  runId: string | null;
  token: string | null;
}

export function readPrintParams(search: string = typeof window !== 'undefined' ? window.location.search : ''): PrintParams {
  const p = new URLSearchParams(search);
  const print = p.get('print') === '1' || p.get('print') === 'true';
  return { print, runId: p.get('run'), token: p.get('rt') };
}

/** Exchange a one-time report token for a short-lived cookie session (a print link opened outside the renderer). */
export async function exchangeReportToken(token: string): Promise<ReportSessionAnswer> {
  const res = await api.post('/auth/report-session', { token });
  return res.data as ReportSessionAnswer;
}

/** Tell the renderer the page is complete (sets data-report-ready="true" on <html>). */
export function markReportReady(): void {
  document.documentElement.removeAttribute('data-report-error');
  document.documentElement.setAttribute('data-report-ready', 'true');
}

/** Tell the renderer the page failed (the PDF fails with this message). */
export function markReportError(message: string): void {
  document.documentElement.setAttribute('data-report-error', String(message || 'report failed').slice(0, 500));
}
